import { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { useNavigate } from 'react-router-dom';
import { AlertCircle, Package, ShoppingBag, TrendingUp, AlertTriangle, ShoppingCart, Bell, Warehouse, Search } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { CardGridSkeleton, TableSkeleton } from '@/components/ui/Skeletons';

const Dashboard = ({ user }) => {
  const [stats, setStats] = useState(null);
  const [reorderAlerts, setReorderAlerts] = useState([]);
  const [myInventory, setMyInventory] = useState([]);
  const [inventorySearch, setInventorySearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [poDialogOpen, setPoDialogOpen] = useState(false);
  const [selectedAlert, setSelectedAlert] = useState(null);
  const [poQuantity, setPoQuantity] = useState('');
  const navigate = useNavigate();
  const isAdmin = user?.role === 'admin' || user?.role === 'manager';
  const assignedWarehouses = user?.assigned_warehouses || [];

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [statsRes, alertsRes, inventoryRes] = await Promise.all([
        axios.get(`${API}/dashboard/stats`),
        axios.get(`${API}/reorder-alerts`),
        axios.get(`${API}/dashboard/my-inventory`)
      ]);
      setStats(statsRes.data);
      // Filter reorder alerts by assigned warehouses for non-admin users
      let alerts = alertsRes.data;
      if (!isAdmin && assignedWarehouses.length > 0) {
        alerts = alerts.filter(a => assignedWarehouses.includes(a.location));
      } else if (!isAdmin && assignedWarehouses.length === 0) {
        alerts = [];
      }
      setReorderAlerts(alerts);
      setMyInventory(inventoryRes.data);
    } catch (error) {
      toast.error('Failed to fetch dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const openCreatePO = (alert) => {
    setSelectedAlert(alert);
    setPoQuantity(alert.suggested_po_qty.toString());
    setPoDialogOpen(true);
  };

  const createPO = async () => {
    try {
      await axios.post(`${API}/purchase-orders`, {
        items: [{
          material_id: selectedAlert.material_id,
          material_name: selectedAlert.material_name,
          quantity: parseFloat(poQuantity),
          unit: selectedAlert.unit
        }],
        supplier: '',
        notes: `Auto-generated from reorder alert. Current stock: ${selectedAlert.current_stock}, Reorder level: ${selectedAlert.reorder_level}`
      });
      toast.success('Purchase Order created successfully!');
      setPoDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create PO');
    }
  };

  const getUrgencyBadge = (urgency) => {
    const styles = {
      CRITICAL: 'bg-red-100 text-red-700 border-red-200',
      HIGH: 'bg-orange-100 text-orange-700 border-orange-200',
      MEDIUM: 'bg-yellow-100 text-yellow-700 border-yellow-200'
    };
    return styles[urgency] || styles.MEDIUM;
  };

  if (loading) {
    return (
      <div className="p-8 space-y-8" data-testid="dashboard-loading">
        <div className="w-1/3">
          <div className="h-10 w-48 bg-stone-200/60 rounded animate-pulse mb-2"></div>
          <div className="h-4 w-64 bg-stone-200/60 rounded animate-pulse"></div>
        </div>
        <CardGridSkeleton count={4} />
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-white p-6 rounded-sm border border-stone-200 shadow-sm min-h-[300px]">
            <div className="h-6 w-32 bg-stone-200/60 rounded animate-pulse mb-6"></div>
            <div className="space-y-4">
              <div className="h-20 w-full bg-stone-200/60 rounded animate-pulse"></div>
              <div className="h-20 w-full bg-stone-200/60 rounded animate-pulse"></div>
              <div className="h-20 w-full bg-stone-200/60 rounded animate-pulse"></div>
            </div>
          </div>
          <div className="bg-white p-6 rounded-sm border border-stone-200 shadow-sm min-h-[300px]">
            <div className="h-6 w-32 bg-stone-200/60 rounded animate-pulse mb-6"></div>
            <div className="h-64 w-full bg-stone-200/60 rounded animate-pulse"></div>
          </div>
        </div>
        <div className="bg-white p-6 rounded-sm border border-stone-200 shadow-sm">
          <TableSkeleton rows={3} columns={6} />
        </div>
      </div>
    );
  }

  const chartData = Object.entries(stats?.production_stage_counts || {}).map(([stage, count]) => ({
    stage: stage.replace('_', ' ').toUpperCase(),
    count,
  }));

  const criticalAlerts = reorderAlerts.filter(a => a.urgency === 'CRITICAL');
  const highAlerts = reorderAlerts.filter(a => a.urgency === 'HIGH');

  return (
    <div className="p-8" data-testid="dashboard">
      <div className="mb-6">
        <h1 className="text-4xl font-semibold text-[hsl(145,60%,10%)] tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }} data-testid="dashboard-title">Dashboard</h1>
        <p className="text-sm text-[hsl(145,20%,40%)] mt-2">
          {isAdmin ? 'Overview of your inventory and production' : `Your warehouses: ${assignedWarehouses.length > 0 ? assignedWarehouses.join(', ') : 'None assigned'}`}
        </p>
      </div>

      {/* Urgent Reorder Alert Banner */}
      {criticalAlerts.length > 0 && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-sm" data-testid="critical-alert-banner">
          <div className="flex items-center gap-2 mb-2">
            <Bell className="text-red-600 animate-pulse" size={20} />
            <h3 className="text-lg font-semibold text-red-700">Critical Stock Alert!</h3>
          </div>
          <p className="text-sm text-red-600 mb-3">{criticalAlerts.length} material(s) are out of stock or critically low. Immediate action required.</p>
          <div className="flex flex-wrap gap-2">
            {criticalAlerts.slice(0, 3).map(alert => (
              <Button
                key={alert.material_id}
                size="sm"
                className="bg-red-600 hover:bg-red-700 text-white"
                onClick={() => openCreatePO(alert)}
                data-testid={`create-po-critical-${alert.material_code}`}
              >
                <ShoppingCart size={14} className="mr-1" />
                Order {alert.material_name}
              </Button>
            ))}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <div
          className="bg-white p-6 rounded-sm border border-stone-200 shadow-sm cursor-pointer hover:shadow-md transition-shadow"
          data-testid="card-low-stock"
          onClick={() => navigate('/raw-materials')}
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-[hsl(145,20%,40%)] uppercase tracking-wider font-bold">Low Stock Alerts</p>
              <p className="text-3xl font-semibold text-[hsl(145,60%,20%)] mt-2" style={{ fontFamily: 'Manrope, sans-serif' }} data-testid="low-stock-count">{stats?.low_stock_count || 0}</p>
            </div>
            <div className="w-12 h-12 bg-[hsl(25,80%,50%)]/10 rounded-sm flex items-center justify-center">
              <AlertCircle className="text-[hsl(25,80%,50%)]" size={24} />
            </div>
          </div>
        </div>

        <div
          className="bg-white p-6 rounded-sm border border-stone-200 shadow-sm cursor-pointer hover:shadow-md transition-shadow"
          data-testid="card-pending-sales"
          onClick={() => navigate('/sales-orders')}
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-[hsl(145,20%,40%)] uppercase tracking-wider font-bold">Pending Sales Orders</p>
              <p className="text-3xl font-semibold text-[hsl(145,60%,20%)] mt-2" style={{ fontFamily: 'Manrope, sans-serif' }} data-testid="pending-sales-count">{stats?.pending_sales_count || 0}</p>
            </div>
            <div className="w-12 h-12 bg-[hsl(145,60%,20%)]/10 rounded-sm flex items-center justify-center">
              <ShoppingBag className="text-[hsl(145,60%,20%)]" size={24} />
            </div>
          </div>
        </div>

        <div
          className="bg-white p-6 rounded-sm border border-stone-200 shadow-sm cursor-pointer hover:shadow-md transition-shadow"
          data-testid="card-active-production"
          onClick={() => navigate('/production')}
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-[hsl(145,20%,40%)] uppercase tracking-wider font-bold">Active Production</p>
              <p className="text-3xl font-semibold text-[hsl(145,60%,20%)] mt-2" style={{ fontFamily: 'Manrope, sans-serif' }} data-testid="active-production-count">{stats?.active_production_count || 0}</p>
            </div>
            <div className="w-12 h-12 bg-[hsl(145,60%,20%)]/10 rounded-sm flex items-center justify-center">
              <TrendingUp className="text-[hsl(145,60%,20%)]" size={24} />
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-sm border border-stone-200 shadow-sm" data-testid="card-delayed-orders">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-[hsl(145,20%,40%)] uppercase tracking-wider font-bold">Delayed Orders</p>
              <p className="text-3xl font-semibold text-red-600 mt-2" style={{ fontFamily: 'Manrope, sans-serif' }} data-testid="delayed-orders-count">{stats?.delayed_orders_count || 0}</p>
            </div>
            <div className="w-12 h-12 bg-red-100 rounded-sm flex items-center justify-center">
              <AlertTriangle className="text-red-600" size={24} />
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Reorder Alerts Section */}
        <div className="bg-white p-6 rounded-sm border border-stone-200 shadow-sm" data-testid="reorder-alerts-section">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <ShoppingCart size={20} className="text-orange-500" />
              <h3 className="text-xl font-semibold text-[hsl(145,60%,10%)]" style={{ fontFamily: 'Manrope, sans-serif' }}>Reorder Alerts</h3>
              {reorderAlerts.length > 0 && (
                <span className="px-2 py-0.5 bg-orange-100 text-orange-700 text-xs font-medium rounded-full">
                  {reorderAlerts.length}
                </span>
              )}
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate('/purchase-orders')}
            >
              View All POs
            </Button>
          </div>
          <div className="space-y-3 max-h-[320px] overflow-y-auto">
            {reorderAlerts.length > 0 ? (
              reorderAlerts.map((alert) => (
                <div
                  key={alert.material_id}
                  className={`p-3 rounded-sm border ${alert.urgency === 'CRITICAL' ? 'bg-red-50 border-red-200' : alert.urgency === 'HIGH' ? 'bg-orange-50 border-orange-200' : 'bg-yellow-50 border-yellow-200'}`}
                  data-testid={`reorder-alert-${alert.material_code}`}
                >
                  <div className="flex justify-between items-start mb-2">
                    <div>
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-medium text-[hsl(145,60%,10%)]">{alert.material_name}</p>
                        <span className={`px-1.5 py-0.5 text-xs font-medium rounded border ${getUrgencyBadge(alert.urgency)}`}>
                          {alert.urgency}
                        </span>
                      </div>
                      <p className="text-xs text-[hsl(145,20%,40%)]">Code: {alert.material_code}</p>
                    </div>
                    <Button
                      size="sm"
                      className="bg-green-600 hover:bg-green-700 text-white"
                      onClick={() => openCreatePO(alert)}
                      data-testid={`create-po-${alert.material_code}`}
                    >
                      <ShoppingCart size={12} className="mr-1" />
                      Create PO
                    </Button>
                  </div>
                  <div className="grid grid-cols-4 gap-2 text-xs">
                    <div>
                      <p className="text-[hsl(145,20%,40%)]">Current</p>
                      <p className={`font-medium ${alert.current_stock <= 0 ? 'text-red-600' : 'text-orange-600'}`}>
                        {alert.current_stock} {alert.unit}
                      </p>
                    </div>
                    <div>
                      <p className="text-[hsl(145,20%,40%)]">Reorder Level</p>
                      <p className="font-medium text-[hsl(145,60%,10%)]">{alert.reorder_level} {alert.unit}</p>
                    </div>
                    <div>
                      <p className="text-[hsl(145,20%,40%)]">MOQ</p>
                      <p className="font-medium text-[hsl(145,60%,10%)]">{alert.moq} {alert.unit}</p>
                    </div>
                    <div>
                      <p className="text-[hsl(145,20%,40%)]">Suggested PO</p>
                      <p className="font-semibold text-green-600">{alert.suggested_po_qty} {alert.unit}</p>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <p className="text-sm text-[hsl(145,20%,40%)] text-center py-4" data-testid="no-reorder-alerts">
                All materials are above reorder levels
              </p>
            )}
          </div>
        </div>

        <div className="bg-white p-6 rounded-sm border border-stone-200 shadow-sm" data-testid="production-flow-chart">
          <h3 className="text-xl font-semibold text-[hsl(145,60%,10%)] mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>Production Flow</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(40,20%,90%)" />
              <XAxis dataKey="stage" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip />
              <Bar dataKey="count" fill="hsl(145,60%,20%)" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="bg-white p-6 rounded-sm border border-stone-200 shadow-sm" data-testid="low-stock-list">
          <div className="flex items-center gap-2 mb-4">
            <Package size={20} className="text-[hsl(25,80%,50%)]" />
            <h3 className="text-xl font-semibold text-[hsl(145,60%,10%)]" style={{ fontFamily: 'Manrope, sans-serif' }}>Low Stock Materials</h3>
          </div>
          <div className="space-y-3 max-h-[280px] overflow-y-auto">
            {stats?.low_stock_materials?.length > 0 ? (
              stats.low_stock_materials.map((material) => (
                <div key={material.id} className="flex justify-between items-center py-2 border-b border-stone-100" data-testid={`low-stock-item-${material.code}`}>
                  <div>
                    <p className="text-sm font-medium text-[hsl(145,60%,10%)]">{material.name}</p>
                    <p className="text-xs text-[hsl(145,20%,40%)]">Code: {material.code} | Location: {material.location || 'N/A'}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-medium text-[hsl(25,80%,50%)]">{material.current_stock} {material.unit}</p>
                    <p className="text-xs text-[hsl(145,20%,40%)]">Reorder: {material.reorder_level}</p>
                  </div>
                </div>
              ))
            ) : (
              <p className="text-sm text-[hsl(145,20%,40%)]" data-testid="no-low-stock">No low stock alerts</p>
            )}
          </div>
        </div>

        {stats?.delayed_orders_count > 0 && (
          <div className="bg-white p-6 rounded-sm border border-red-200 shadow-sm" data-testid="delayed-orders-list">
            <div className="flex items-center gap-2 mb-4">
              <AlertTriangle size={20} className="text-red-600" />
              <h3 className="text-xl font-semibold text-red-600" style={{ fontFamily: 'Manrope, sans-serif' }}>Delayed Orders</h3>
            </div>
            <div className="space-y-3 max-h-[280px] overflow-y-auto">
              {stats.delayed_orders.map((order) => (
                <div key={order.id} className="flex justify-between items-center py-2 border-b border-red-100" data-testid={`delayed-order-${order.so_number}`}>
                  <div>
                    <p className="text-sm font-medium text-[hsl(145,60%,10%)]">{order.so_number}</p>
                    <p className="text-xs text-[hsl(145,20%,40%)]">{order.customer}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-medium text-red-600">Overdue</p>
                    <p className="text-xs text-[hsl(145,20%,40%)]">Expected: {new Date(order.expected_delivery_date).toLocaleDateString()}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* My Inventory Section */}
      {myInventory.length > 0 && (
        <div className="mt-6 bg-white p-6 rounded-sm border border-stone-200 shadow-sm" data-testid="my-inventory-section">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Warehouse size={20} className="text-[hsl(145,60%,20%)]" />
              <h3 className="text-xl font-semibold text-[hsl(145,60%,10%)]" style={{ fontFamily: 'Manrope, sans-serif' }}>
                {isAdmin ? 'All Warehouse Inventory' : 'My Warehouse Inventory'}
              </h3>
              <span className="px-2 py-0.5 bg-[hsl(145,60%,20%)]/10 text-[hsl(145,60%,20%)] text-xs font-medium rounded-full">
                {myInventory.length} items
              </span>
            </div>
            <div className="relative w-64">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-stone-400" size={16} />
              <Input
                placeholder="Search materials..."
                value={inventorySearch}
                onChange={(e) => setInventorySearch(e.target.value)}
                className="pl-9 h-9 text-sm"
              />
            </div>
          </div>
          {/* Desktop Table View */}
          <div className="hidden md:block overflow-x-auto max-h-[400px] overflow-y-auto mt-4">
            <table className="w-full text-sm">
              <thead className="bg-stone-50 sticky top-0 z-10">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-stone-500 uppercase tracking-wider">Code</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-stone-500 uppercase tracking-wider">Material</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-stone-500 uppercase tracking-wider">Location</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-stone-500 uppercase tracking-wider">Quantity</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-stone-500 uppercase tracking-wider">Reorder Level</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-stone-500 uppercase tracking-wider">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-stone-100">
                {myInventory
                  .filter(item => {
                    if (!inventorySearch) return true;
                    const q = inventorySearch.toLowerCase();
                    return item.material_code?.toLowerCase().includes(q) ||
                      item.material_name?.toLowerCase().includes(q) ||
                      item.location?.toLowerCase().includes(q);
                  })
                  .map((item, idx) => (
                    <tr key={`${item.material_id}-${item.location}-${idx}`} className="hover:bg-stone-50/50 transition-colors">
                      <td className="px-4 py-3 font-medium text-stone-700">{item.material_code}</td>
                      <td className="px-4 py-3 text-stone-700">{item.material_name}</td>
                      <td className="px-4 py-3">
                        <span className="px-2 py-0.5 bg-blue-50 text-blue-700 text-xs font-medium rounded">
                          {item.location}
                        </span>
                      </td>
                      <td className={`px-4 py-3 text-right font-medium ${item.is_low_stock ? 'text-red-600' : 'text-stone-700'}`}>
                        {typeof item.quantity === 'number' ? item.quantity.toLocaleString() : item.quantity} {item.unit}
                      </td>
                      <td className="px-4 py-3 text-right text-stone-500">
                        {item.reorder_level} {item.unit}
                      </td>
                      <td className="px-4 py-3 text-center">
                        {item.is_low_stock ? (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-red-50 text-red-700 text-xs font-medium rounded-full border border-red-200">
                            <AlertTriangle size={12} /> Low
                          </span>
                        ) : (
                          <span className="inline-flex items-center px-2 py-0.5 bg-green-50 text-green-700 text-xs font-medium rounded-full border border-green-200">
                            OK
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>

          {/* Mobile Card View */}
          <div className="md:hidden mt-4 space-y-4 max-h-[500px] overflow-y-auto">
            {myInventory
              .filter(item => {
                if (!inventorySearch) return true;
                const q = inventorySearch.toLowerCase();
                return item.material_code?.toLowerCase().includes(q) ||
                  item.material_name?.toLowerCase().includes(q) ||
                  item.location?.toLowerCase().includes(q);
              })
              .map((item, idx) => (
                <div key={`mobile-${item.material_id}-${item.location}-${idx}`} className="bg-white border border-stone-200 rounded-lg p-4 shadow-sm">
                  <div className="flex justify-between items-start mb-2">
                    <div>
                      <h4 className="font-semibold text-stone-900">{item.material_name}</h4>
                      <p className="text-xs text-stone-500">{item.material_code}</p>
                    </div>
                    {item.is_low_stock ? (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-red-50 text-red-700 text-xs font-medium rounded-full border border-red-200 whitespace-nowrap">
                        <AlertTriangle size={10} /> Low
                      </span>
                    ) : (
                      <span className="inline-flex items-center px-2 py-0.5 bg-green-50 text-green-700 text-xs font-medium rounded-full border border-green-200 whitespace-nowrap">
                        OK
                      </span>
                    )}
                  </div>

                  <div className="mt-3 grid grid-cols-2 gap-y-2 gap-x-4 text-sm">
                    <div>
                      <span className="text-stone-500 block text-xs">Location</span>
                      <span className="font-medium text-stone-700">{item.location}</span>
                    </div>
                    <div>
                      <span className="text-stone-500 block text-xs">Quantity</span>
                      <span className={`font-medium ${item.is_low_stock ? 'text-red-600' : 'text-stone-700'}`}>
                        {typeof item.quantity === 'number' ? item.quantity.toLocaleString() : item.quantity} {item.unit}
                      </span>
                    </div>
                    <div className="col-span-2">
                      <span className="text-stone-500 block text-xs">Reorder Level</span>
                      <span className="text-stone-700">{item.reorder_level} {item.unit}</span>
                    </div>
                  </div>
                </div>
              ))}
          </div>
        </div>
      )}

      {/* Create PO Dialog */}
      <Dialog open={poDialogOpen} onOpenChange={setPoDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create Purchase Order</DialogTitle>
          </DialogHeader>
          {selectedAlert && (
            <div className="space-y-4">
              <div className="p-4 bg-stone-50 rounded-sm">
                <p className="text-sm font-medium text-[hsl(145,60%,10%)]">{selectedAlert.material_name}</p>
                <p className="text-xs text-[hsl(145,20%,40%)]">Code: {selectedAlert.material_code}</p>
                <div className="grid grid-cols-2 gap-4 mt-3 text-xs">
                  <div>
                    <p className="text-[hsl(145,20%,40%)]">Current Stock</p>
                    <p className={`font-medium ${selectedAlert.current_stock <= 0 ? 'text-red-600' : 'text-orange-600'}`}>
                      {selectedAlert.current_stock} {selectedAlert.unit}
                    </p>
                  </div>
                  <div>
                    <p className="text-[hsl(145,20%,40%)]">Reorder Level</p>
                    <p className="font-medium">{selectedAlert.reorder_level} {selectedAlert.unit}</p>
                  </div>
                  <div>
                    <p className="text-[hsl(145,20%,40%)]">MOQ</p>
                    <p className="font-medium">{selectedAlert.moq} {selectedAlert.unit}</p>
                  </div>
                  <div>
                    <p className="text-[hsl(145,20%,40%)]">Max Order Qty</p>
                    <p className="font-medium">{selectedAlert.max_qty} {selectedAlert.unit}</p>
                  </div>
                </div>
              </div>

              <div>
                <Label htmlFor="po_quantity">Order Quantity ({selectedAlert.unit})</Label>
                <Input
                  id="po_quantity"
                  type="number"
                  step="0.01"
                  min={selectedAlert.moq}
                  max={selectedAlert.max_qty > 0 ? selectedAlert.max_qty : undefined}
                  value={poQuantity}
                  onChange={(e) => setPoQuantity(e.target.value)}
                  className="mt-1"
                />
                <p className="text-xs text-green-600 mt-1">
                  Suggested quantity: {selectedAlert.suggested_po_qty} {selectedAlert.unit} (based on MOQ)
                </p>
              </div>

              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setPoDialogOpen(false)}>Cancel</Button>
                <Button className="bg-green-600 hover:bg-green-700 text-white" onClick={createPO}>
                  <ShoppingCart size={14} className="mr-2" />
                  Create Purchase Order
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Dashboard;